import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Store } from "../../../__generated__/graphql";
import {
  GET_PICKUP_NOW_COUNT,
  GET_PICKUP_NOW_STORES,
} from "../api/home-queries";

/** store-filter.ts의 processAvailableFilter / STORE_FILTER와 동일한 값 */
type AvailableFilter = "ALL" | "AVAILABLE" | "PICKUP_NOW";

interface UsePickupNowStoresParams {
  lat: number | null;
  lng: number | null;
  availableFilter: AvailableFilter;
  setAvailableFilter: (filter: AvailableFilter) => void;
}

/**
 * 바로픽업 필터용: 개수 조회, 매장 목록 조회, 로딩/빈 결과 처리.
 * 데이터가 로드된 후 바로픽업이 선택되어 있지만 매장이 없으면 구매가능(AVAILABLE)으로 전환.
 */
export function usePickupNowStores({
  lat,
  lng,
  availableFilter,
  setAvailableFilter,
}: UsePickupNowStoresParams) {
  const [pickupNowStores, setPickupNowStores] = useState<Store[] | null>(null);
  const [isLoadingPickupNow, setIsLoadingPickupNow] = useState(false);

  const hasLocation = lat != null && lng != null;
  const shouldFetchPickupNow =
    hasLocation && availableFilter === "PICKUP_NOW";

  // 바로픽업 개수 표기를 위한 쿼리
  const { data: pickupNowCountData } = useQuery(GET_PICKUP_NOW_COUNT, {
    variables: { lat, lng, limit: 1000, offset: 0 },
    skip: !hasLocation,
  });

  // 바로픽업 필터 선택 시 API 호출 (매장 목록)
  const { data: pickupNowStoresData, loading: isLoadingPickupNowQuery } =
    useQuery(GET_PICKUP_NOW_STORES, {
      variables: { lat, lng, limit: 1000, offset: 0 },
      skip: !shouldFetchPickupNow,
    });

  const pickupNowCount = pickupNowCountData?.storesV2PickupNow?.length ?? 0;
  const hasPickupNowStores = pickupNowCount > 0;

  // 쿼리 결과를 로컬 상태에 동기화 (바로픽업 해제 시 null로 초기화)
  useEffect(() => {
    if (pickupNowStoresData?.storesV2PickupNow) {
      setPickupNowStores(pickupNowStoresData.storesV2PickupNow as Store[]);
      setIsLoadingPickupNow(false);
    } else if (availableFilter !== "PICKUP_NOW") {
      setPickupNowStores(null);
    }
  }, [pickupNowStoresData, availableFilter]);

  useEffect(() => {
    if (availableFilter === "PICKUP_NOW") {
      setIsLoadingPickupNow(isLoadingPickupNowQuery);
    }
  }, [availableFilter, isLoadingPickupNowQuery]);

  // 데이터가 로드된 후 바로픽업 필터가 선택되어 있지만 매장이 없으면 구매가능으로 변경
  useEffect(() => {
    if (
      availableFilter === "PICKUP_NOW" &&
      !isLoadingPickupNow &&
      pickupNowStores &&
      pickupNowStores.length === 0
    ) {
      setAvailableFilter("AVAILABLE");
      localStorage.setItem("availableFilter", "AVAILABLE");
    }
  }, [availableFilter, isLoadingPickupNow, pickupNowStores, setAvailableFilter]);

  return {
    pickupNowStores,
    setPickupNowStores,
    isLoadingPickupNow,
    pickupNowCount,
    hasPickupNowStores,
  };
}
